import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Path, CaretRight, MapTrifold } from '@phosphor-icons/react';
import { listTreks, deleteTrek } from '../lib/db';
import { totalDistance, fmtDistance, fmtDuration } from '../lib/geo';
import OfflineBadge from '../components/OfflineBadge';
import { toast } from 'sonner';

const EMPTY_BG = 'https://images.unsplash.com/photo-1586022045315-1cdd6493045c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxoaWtlciUyMGF0JTIwbmlnaHR8ZW58MHx8fHwxNzg3NzYxMzQxfDA&ixlib=rb-4.1.0&q=85';

export default function MyTreks() {
  const [treks, setTreks] = useState(null);

  const load = async () => {
    const list = await listTreks();
    setTreks(list);
  };

  useEffect(() => { load(); }, []);

  const remove = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    await deleteTrek(id);
    toast('Trek deleted');
    load();
  };

  return (
    <div className="min-h-[100dvh] pb-28">
      <header className="safe-top px-5 pt-4 flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">Trek log</div>
          <h1 className="font-display font-black text-3xl" data-testid="treks-title">My Treks</h1>
        </div>
        <OfflineBadge />
      </header>

      <div className="px-5 mt-6">
        {treks == null && <div className="text-zinc-400 text-sm">Loading…</div>}
        {treks && treks.length === 0 && (
          <div className="glass-panel rounded-2xl p-6 text-center">
            <img src={EMPTY_BG} alt="" className="w-full h-40 object-cover rounded-xl opacity-70 mb-4" />
            <div className="font-display font-bold text-lg">No treks yet</div>
            <div className="text-xs text-zinc-400 mt-1">Start a trek from Home to record your first trail.</div>
          </div>
        )}
        {treks && treks.length > 0 && (
          <ul className="space-y-3" data-testid="trek-list">
            {treks.map((t) => {
              const dist = totalDistance(t.points || []);
              const dur = (t.endedAt || Date.now()) - t.startedAt;
              return (
                <li key={t.id} data-testid={`trek-item-${t.id}`}>
                  <Link to={`/treks/${t.id}`} className="btn-tap block glass-panel rounded-2xl p-4 active:bg-white/5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          {t.isDemo && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Demo</span>}
                          {t.simulated && !t.isDemo && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">Sim</span>}
                          <MapTrifold size={14} weight="bold" className="text-[#FF9F1C]" />
                        </div>
                        <div className="font-display font-bold text-base truncate mt-1">{t.name}</div>
                        <div className="text-[11px] text-zinc-500 mt-0.5">{new Date(t.startedAt).toLocaleString()}</div>
                        <div className="grid grid-cols-3 gap-2 mt-3">
                          <StatRow label="Distance" value={fmtDistance(dist)} />
                          <StatRow label="Time" value={fmtDuration(dur)} />
                          <StatRow label="Points" value={t.points?.length ?? 0} />
                        </div>
                      </div>
                      <CaretRight size={18} className="text-zinc-500 shrink-0 mt-1" />
                    </div>
                  </Link>
                  <button
                    onClick={() => remove(t.id, t.name)}
                    className="mt-1 ml-2 text-[10px] text-rose-400 uppercase tracking-widest hover:underline"
                    data-testid={`delete-${t.id}`}
                  >
                    Delete
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatRow({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-mono-stat text-sm font-bold">{value}</div>
    </div>
  );
}
